"use client";

import {
	Avatar,
	Button,
	type ColProps,
	Col as DSCol,
	Grid,
	Heading,
	Link,
	type LinkProps,
	RichText,
	Text
} from "@pamperedchef/design-system";
import { type FC, type ReactNode } from "react";

import * as styles from "./ConsultantSlimBanner.module.scss";

const Col = DSCol as FC<ColProps & { children?: ReactNode }>;

export type HeadingTag = "h1" | "h2" | "h3" | "h4";

export interface ConsultantSlimBannerCta {
	children: React.ReactNode;
	href?: string;
	onPress?: () => void;
}

export interface ConsultantSlimBannerProps {
	headline?: string;
	headlineTag?: HeadingTag;
	description?: string;
	consultant?: {
		consultantNumber?: string;
		image?: string;
		name: string;
		location?: string;
	};
	consultantLabel?: string;
	cta?: ConsultantSlimBannerCta;
	secondaryCta?: LinkProps;
	className?: string;
}

function cx(...classes: Array<string | false | null | undefined>): string {
	return classes.filter(Boolean).join(" ");
}

export function ConsultantSlimBanner({
	headline,
	headlineTag = "h2",
	description,
	consultant,
	consultantLabel = "Your Consultant:",
	cta,
	secondaryCta,
	className
}: ConsultantSlimBannerProps): JSX.Element {
	const isConnected = Boolean(consultant?.name);
	const hasCta = Boolean(cta || secondaryCta);

	const handleCtaPress = cta?.onPress
		? cta.onPress
		: cta?.href
			? () => {
					window.location.href = cta.href as string;
				}
			: undefined;

	return (
		<section
			className={cx(
				styles["banner"],
				!isConnected && styles["banner--not-connected"],
				className
			)}
			data-component="ConsultantSlimBanner"
		>
			<Grid columns={12} gap="lg">
				<Col size={12} xl={hasCta ? 9 : 12}>
					<div className={styles["main"]}>
						{isConnected && consultant?.image ? (
							<Avatar
								className={styles["avatar"]}
								src={consultant.image}
								alt={consultant.name}
								size="xl"
							/>
						) : null}

						<div className={styles["text"]}>
							{headline ? (
								<Heading
									className={`display-sm ${styles["headline"]}`}
									tag={headlineTag}
								>
									{headline}
								</Heading>
							) : null}

							<div className={styles["body"]}>
								{description ? (
									<RichText
										className={styles["description"]}
										content={description}
									/>
								) : null}

								{isConnected && consultant?.name ? (
									<Text
										className={styles["consultant"]}
										styledAs="mdr"
									>
										<strong>{consultantLabel}</strong>{" "}
										{consultant.name}
									</Text>
								) : null}
							</div>
						</div>
					</div>
				</Col>

				{hasCta ? (
					<Col size={12} xl={3}>
						<div className={styles["ctas"]}>
							{cta ? (
								<Button
									className={styles["primary-cta"]}
									kind="primary"
									size="lg"
									surface="default"
									onPress={handleCtaPress}
								>
									{cta.children}
								</Button>
							) : null}

							{secondaryCta ? (
								<Link
									{...secondaryCta}
									className={cx(
										styles["secondary-cta"],
										secondaryCta.className
									)}
								/>
							) : null}
						</div>
					</Col>
				) : null}
			</Grid>
		</section>
	);
}
